import { useEffect, useState } from "react";
import { HugeiconsIcon } from "@hugeicons/react";
import { useAuth } from "@clerk/react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { AnimatePresence, motion } from "framer-motion";
import { useUser } from "@clerk/react";
import { createNewsApi } from "@/lib/api/news";
import { createNotificationsApi } from "@/lib/api/notifications";
import {
  DarkModeIcon,
  Sun01Icon,
  Moon01Icon,
  ComputerIcon,
  HelpCircleIcon,
  Home01Icon,
  Logout01Icon,
  ArrowUpRight03Icon,
  Mailbox01Icon,
  Settings02Icon,
  CheckmarkCircle01Icon,
  Megaphone01Icon,
  SparklesIcon,
} from "@hugeicons/core-free-icons";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Loader2 } from "lucide-react";

// Dummy user removed as we now use real Clerk user data

type Theme = "light" | "dark" | "system";

function useTheme() {
  const [theme, setThemeState] = useState<Theme>(() => {
    if (typeof window === "undefined") return "dark";
    return (localStorage.getItem("theme") as Theme) ?? "dark";
  });

  useEffect(() => {
    const root = document.documentElement;
    localStorage.setItem("theme", theme);

    if (theme === "system") {
      const prefersDark = window.matchMedia(
        "(prefers-color-scheme: dark)",
      ).matches;
      root.classList.toggle("dark", prefersDark);
    } else {
      root.classList.toggle("dark", theme === "dark");
    }
  }, [theme]);

  return { theme, setTheme: setThemeState };
}

function formatRelativeTime(dateString: string) {
  const date = new Date(dateString);
  const now = new Date();
  const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);

  if (diffInSeconds < 60) return "Just now";
  const diffInMinutes = Math.floor(diffInSeconds / 60);
  if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
  const diffInHours = Math.floor(diffInMinutes / 60);
  if (diffInHours < 24) return `${diffInHours}h ago`;
  const diffInDays = Math.floor(diffInHours / 24);
  if (diffInDays < 7) return `${diffInDays}d ago`;

  return date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

export function NavUser() {
  const { theme, setTheme } = useTheme();
  const { getToken, signOut } = useAuth();
  const { user } = useUser();

  const newsApi = createNewsApi({ getToken });
  const notificationsApi = createNotificationsApi({ getToken });

  const { data: newsItems = [], isLoading: isNewsLoading } = useQuery({
    queryKey: ["news"],
    queryFn: () => newsApi.list(),
  });

  const { data: notifications = [], isLoading: isNotificationsLoading } =
    useQuery({
      queryKey: ["notifications"],
      queryFn: () => notificationsApi.list(),
    });

  const queryClient = useQueryClient();

  const markAsReadMutation = useMutation({
    mutationFn: (id: string) => notificationsApi.markAsRead(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["notifications"] });
    },
  });

  const handleNotificationClick = (
    _e: React.MouseEvent<HTMLAnchorElement>,
    n: any,
  ) => {
    if (!n.is_read) {
      markAsReadMutation.mutate(n.id);
    }
  };

  const unreadCount = notifications.filter((n) => !n.is_read).length;

  return (
    <div className="flex items-center justify-between px-2 py-2 group-data-[collapsible=icon]:flex-col group-data-[collapsible=icon]:items-center group-data-[collapsible=icon]:gap-2 group-data-[collapsible=icon]:px-0">
      {/* User avatar — opens menu dropdown */}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button className="cursor-pointer hover:bg-sidebar-accent dark:hover:bg-surface-raised-hover flex size-8 items-center justify-center rounded-lg transition-colors">
            <Avatar size="sm">
              <AvatarImage src={user?.imageUrl} alt={user?.fullName || ""} />
              <AvatarFallback className="text-[10px]">
                {user?.fullName
                  ? user.fullName
                      .split(" ")
                      .map((n) => n[0])
                      .join("")
                  : ""}
              </AvatarFallback>
            </Avatar>
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          className="min-w-64"
          side="top"
          align="start"
          sideOffset={8}
        >
          {/* User info header */}
          <div className="flex items-center gap-3 px-2.5 py-2.5">
            <Avatar size="sm">
              <AvatarImage src={user?.imageUrl} alt={user?.fullName || ""} />
              <AvatarFallback className="text-[10px]">
                {user?.fullName
                  ? user.fullName
                      .split(" ")
                      .map((n) => n[0])
                      .join("")
                  : ""}
              </AvatarFallback>
            </Avatar>
            <div className="flex flex-col">
              <span className="text-sm font-medium text-surface-raised-text">
                {user?.fullName}
              </span>
              <span className="text-xs text-surface-raised-text-muted">
                {user?.primaryEmailAddress?.emailAddress}
              </span>
            </div>
          </div>

          <DropdownMenuSeparator />

          <DropdownMenuItem asChild>
            <a href="/settings">
              <HugeiconsIcon icon={Settings02Icon} className="size-4" />
              Settings
            </a>
          </DropdownMenuItem>

          {/* Appearance with submenu */}
          <DropdownMenuSub>
            <DropdownMenuSubTrigger>
              <HugeiconsIcon icon={DarkModeIcon} className="size-4" />
              Appearance
            </DropdownMenuSubTrigger>
            <DropdownMenuSubContent>
              <DropdownMenuItem onClick={() => setTheme("light")}>
                <HugeiconsIcon icon={Sun01Icon} className="size-4" />
                Light
                {theme === "light" && (
                  <span className="ml-auto text-xs">✓</span>
                )}
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setTheme("dark")}>
                <HugeiconsIcon icon={Moon01Icon} className="size-4" />
                Dark
                {theme === "dark" && <span className="ml-auto text-xs">✓</span>}
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setTheme("system")}>
                <HugeiconsIcon icon={ComputerIcon} className="size-4" />
                System
                {theme === "system" && (
                  <span className="ml-auto text-xs">✓</span>
                )}
              </DropdownMenuItem>
            </DropdownMenuSubContent>
          </DropdownMenuSub>

          <DropdownMenuItem asChild>
            <a href="/support" target="_blank" rel="noopener noreferrer">
              <HugeiconsIcon icon={HelpCircleIcon} className="size-4" />
              Support
              <HugeiconsIcon
                icon={ArrowUpRight03Icon}
                // className="ml-auto size-3 opacity-50"
                className="ml-auto size-4"
              />
            </a>
          </DropdownMenuItem>
          {/* TODO: We can uncomment and build native docs/forum pages once we decide on the stack 
          <DropdownMenuItem>
            <HugeiconsIcon icon={Book01Icon} className="size-4" />
            Documentation
            <HugeiconsIcon
              icon={ArrowRight01Icon}
              className="ml-auto size-3 opacity-50"
            />
          </DropdownMenuItem>
          <DropdownMenuItem>
            <HugeiconsIcon icon={UserMultipleIcon} className="size-4" />
            Community
          </DropdownMenuItem>
          */}
          <DropdownMenuItem asChild>
            <a href="/">
              <HugeiconsIcon icon={Home01Icon} className="size-4" />
              Homepage
            </a>
          </DropdownMenuItem>

          <DropdownMenuSeparator />

          <DropdownMenuItem onClick={() => signOut({ redirectUrl: "/" })}>
            <HugeiconsIcon icon={Logout01Icon} className="size-4" />
            Sign out
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Messages icon — opens notifications panel */}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button className="text-muted-foreground hover:text-foreground hover:bg-sidebar-accent dark:hover:bg-surface-raised-hover flex size-8 items-center justify-center rounded-lg transition-colors relative">
            <HugeiconsIcon icon={Mailbox01Icon} className="size-4" />
            {unreadCount > 0 && (
              <span className="absolute top-1 right-1 size-2 rounded-full bg-red-500 shadow-[0_0_8px_rgba(239,68,68,0.8)] animate-pulse" />
            )}
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          className="w-[340px] p-0 shadow-2xl rounded-xl border-border bg-linear-to-b from-background to-background/95 backdrop-blur-xl z-50 overflow-hidden"
          side="right"
          align="end"
          sideOffset={8}
        >
          <Tabs defaultValue="whats-new" className="gap-0 w-full">
            <TabsList className="w-full flex p-1 bg-muted/40 rounded-t-xl rounded-b-none border-b border-border/50">
              <TabsTrigger
                value="inbox"
                className="flex-1 rounded-md text-xs font-medium data-[state=active]:bg-background data-[state=active]:shadow-sm transition-all"
              >
                Inbox{" "}
                {unreadCount > 0 && (
                  <span className="ml-1.5 bg-primary/20 text-primary px-1.5 py-0.5 rounded-full text-[10px] leading-tight">
                    {unreadCount}
                  </span>
                )}
              </TabsTrigger>
              <TabsTrigger
                value="whats-new"
                className="flex-1 rounded-md text-xs font-medium data-[state=active]:bg-background data-[state=active]:shadow-sm transition-all"
              >
                What&apos;s new
              </TabsTrigger>
            </TabsList>

            <TabsContent
              value="inbox"
              className="m-0 max-h-[400px] overflow-y-auto outline-none scrollbar-thin"
            >
              {isNotificationsLoading ? (
                <div className="flex h-32 items-center justify-center">
                  <Loader2 className="size-5 animate-spin text-muted-foreground" />
                </div>
              ) : notifications.length === 0 ? (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="flex flex-col items-center justify-center py-12 px-4 text-center"
                >
                  <div className="size-12 rounded-full bg-muted/50 flex items-center justify-center mb-4">
                    <HugeiconsIcon
                      icon={CheckmarkCircle01Icon}
                      className="size-6 text-muted-foreground/60"
                    />
                  </div>
                  <p className="text-sm font-medium text-foreground">
                    You&apos;re all caught up
                  </p>
                  <p className="text-xs text-muted-foreground mt-1 max-w-[200px]">
                    No new messages or alerts at the moment.
                  </p>
                </motion.div>
              ) : (
                <div className="flex flex-col divide-y divide-border/40 pb-2">
                  <AnimatePresence>
                    {notifications.map((n, i) => (
                      <motion.a
                        key={n.id}
                        href={n.action_url || "#"}
                        onClick={(e) => handleNotificationClick(e, n)}
                        initial={{ opacity: 0, x: -10 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: i * 0.05 }}
                        className="flex items-start gap-4 p-4 hover:bg-muted/30 transition-colors group relative"
                      >
                        {!n.is_read && (
                          <div className="absolute left-2 top-1/2 -translate-y-1/2 size-1.5 rounded-full bg-blue-500 shadow-[0_0_8px_rgba(59,130,246,0.6)]" />
                        )}
                        <div className="flex-1 min-w-0 pl-2">
                          <div className="flex items-baseline justify-between gap-2 mb-1">
                            <h4
                              className={`text-sm tracking-tight truncate ${n.is_read ? "font-medium text-foreground/80" : "font-semibold text-foreground"}`}
                            >
                              {n.title}
                            </h4>
                            <span className="text-[10px] text-muted-foreground shrink-0 whitespace-nowrap">
                              {formatRelativeTime(n.created_at)}
                            </span>
                          </div>
                          <p
                            className={`text-xs leading-relaxed line-clamp-2 ${n.is_read ? "text-muted-foreground/80" : "text-muted-foreground"}`}
                          >
                            {n.message}
                          </p>
                        </div>
                      </motion.a>
                    ))}
                  </AnimatePresence>
                </div>
              )}
            </TabsContent>

            <TabsContent
              value="whats-new"
              className="m-0 max-h-[400px] overflow-y-auto outline-none scrollbar-thin bg-surface-raised/30"
            >
              {isNewsLoading ? (
                <div className="flex h-32 items-center justify-center">
                  <Loader2 className="size-5 animate-spin text-muted-foreground" />
                </div>
              ) : newsItems.length === 0 ? (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="flex flex-col items-center justify-center py-12 px-4 text-center"
                >
                  <div className="size-12 rounded-full bg-muted/50 flex items-center justify-center mb-4">
                    <HugeiconsIcon
                      icon={Megaphone01Icon}
                      className="size-6 text-muted-foreground/60"
                    />
                  </div>
                  <p className="text-sm font-medium text-foreground">
                    No updates yet
                  </p>
                  <p className="text-xs text-muted-foreground mt-1 max-w-[200px]">
                    Check back later for new features and announcements.
                  </p>
                </motion.div>
              ) : (
                <div className="flex flex-col p-3 gap-3">
                  <AnimatePresence>
                    {newsItems.map((item, i) => (
                      <motion.a
                        key={item.id}
                        href={item.action_url || "#"}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: i * 0.05 }}
                        className="group flex flex-col gap-3 rounded-xl border border-border/50 bg-background/50 p-3 hover:bg-muted/30 transition-all hover:border-border hover:shadow-sm overflow-hidden relative"
                      >
                        {item.gradient ? (
                          <div
                            className={`h-24 w-full rounded-lg bg-gradient-to-br ${item.gradient} opacity-90 group-hover:opacity-100 transition-opacity`}
                          />
                        ) : (
                          <div className="h-24 w-full rounded-lg bg-muted/50 flex items-center justify-center">
                            <HugeiconsIcon
                              icon={SparklesIcon}
                              className="size-6 text-muted-foreground/30"
                            />
                          </div>
                        )}
                        <div className="space-y-1.5 px-1">
                          <div className="flex items-center justify-between">
                            <span className="px-2 py-0.5 rounded-full bg-primary/10 text-primary text-[10px] font-semibold tracking-wide uppercase">
                              Update
                            </span>
                            <span className="text-[10px] text-muted-foreground font-medium">
                              {formatRelativeTime(item.created_at)}
                            </span>
                          </div>
                          <h4 className="text-sm font-semibold text-foreground leading-tight group-hover:text-primary transition-colors">
                            {item.title}
                          </h4>
                          <p className="text-xs text-muted-foreground leading-relaxed line-clamp-2">
                            {item.description}
                          </p>
                        </div>
                      </motion.a>
                    ))}
                  </AnimatePresence>
                </div>
              )}
            </TabsContent>
          </Tabs>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}
